import packageInfo from "../package.json";
import { SearchVariables } from "./hooks/makeSearch";
import { ListSettings, ListViews, Pagination } from "./types";

export const APP_MOUNT_URI = process.env.APP_MOUNT_URI || "/";
export const APP_DEFAULT_URI = "/";
export const API_URI = process.env.API_URI;
export const SW_INTERVAL = parseInt(process.env.SW_INTERVAL, 10);
export const IS_CLOUD_INSTANCE = process.env.IS_CLOUD_INSTANCE === "true";

export const DEFAULT_INITIAL_SEARCH_DATA: SearchVariables = {
  after: null,
  first: 20,
  query: ""
};

export const DEFAULT_INITIAL_PAGINATION_DATA: Pagination = {
  after: undefined,
  before: undefined
};

export const PAGINATE_BY = 20;
export const CUSTOMER_ORDER_BY = 50;
export const VALUES_PAGINATE_BY = 10;

export type ProductListColumns =
  | "productType"
  | "availability"
  | "price"
  | "date";

export interface AppListViewSettings {
  [ListViews.APPS_LIST]: ListSettings;
  [ListViews.ATTRIBUTE_VALUE_LIST]: ListSettings;
  [ListViews.CATEGORY_LIST]: ListSettings;
  [ListViews.COLLECTION_LIST]: ListSettings;
  [ListViews.CUSTOMER_LIST]: ListSettings;
  [ListViews.CUSTOMER_ORDER]: ListSettings;
  [ListViews.DRAFT_LIST]: ListSettings;
  [ListViews.NAVIGATION_LIST]: ListSettings;
  [ListViews.ORDER_LIST]: ListSettings;
  [ListViews.PAGES_LIST]: ListSettings;
  [ListViews.PLUGINS_LIST]: ListSettings;
  [ListViews.PRODUCT_LIST]: ListSettings<ProductListColumns>;
  [ListViews.SALES_LIST]: ListSettings;
  [ListViews.SHIPPING_METHODS_LIST]: ListSettings;
  [ListViews.STAFF_MEMBERS_LIST]: ListSettings;
  [ListViews.PERMISSION_GROUP_LIST]: ListSettings;
  [ListViews.VOUCHER_LIST]: ListSettings;
  [ListViews.WAREHOUSE_LIST]: ListSettings;
  [ListViews.WEBHOOK_LIST]: ListSettings;
  [ListViews.TRANSLATION_ATTRIBUTE_VALUE_LIST]: ListSettings;
  [ListViews.GIFT_CARD_LIST]: ListSettings;
  [ListViews.SOLD_TO_LIST]: ListSettings;
  [ListViews.CREATE_ORDER_PRODUCT_LIST]: ListSettings;
  [ListViews.EXPORT_PI_LIST]: ListSettings;
  [ListViews.EXPORT_ORDER]: ListSettings;
  [ListViews.CREATE_ORDER_DOMESTIC_LIST]: ListSettings;
  [ListViews.ORDER_EXPORT_SOLD_TO_LIST]: ListSettings;
  [ListViews.ORDER_EXPORT_DETAIL_LIST]: ListSettings;
  [ListViews.USER_MANAGEMENT]: ListSettings;
  [ListViews.EDIT_ORDER]: ListSettings;
  [ListViews.EDIT_ORDER_DOMESTIC]: ListSettings;
  [ListViews.APT_CTP_LIST]: ListSettings;
  [ListViews.REQUIREDATTENTION]: ListSettings;
  [ListViews.FILTER_ORDER]: ListSettings;
  [ListViews.ORDER_CONFIRM_CUSTOMER]: ListSettings;
  [ListViews.ORDER_CONFIRM_ADMIN]: ListSettings;
  [ListViews.DELIVERY_REPORT_CUSTOMER]: ListSettings;
  [ListViews.LOG_CHANGE]: ListSettings;
}

export const defaultListSettings: AppListViewSettings = {
  [ListViews.APPS_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ATTRIBUTE_VALUE_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.CATEGORY_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.COLLECTION_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.CUSTOMER_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.CUSTOMER_ORDER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.DRAFT_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.NAVIGATION_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ORDER_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.PAGES_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.PLUGINS_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.PRODUCT_LIST]: {
    columns: ["availability", "price", "productType", "date"],
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.SALES_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.SHIPPING_METHODS_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.STAFF_MEMBERS_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.PERMISSION_GROUP_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.VOUCHER_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.WAREHOUSE_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.WEBHOOK_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.TRANSLATION_ATTRIBUTE_VALUE_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.GIFT_CARD_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.SOLD_TO_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.CREATE_ORDER_PRODUCT_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.EXPORT_PI_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.USER_MANAGEMENT]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.EXPORT_ORDER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.CREATE_ORDER_DOMESTIC_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ORDER_EXPORT_SOLD_TO_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ORDER_EXPORT_DETAIL_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.EDIT_ORDER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.EDIT_ORDER_DOMESTIC]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.APT_CTP_LIST]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.REQUIREDATTENTION]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.FILTER_ORDER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ORDER_CONFIRM_ADMIN]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.ORDER_CONFIRM_CUSTOMER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.DELIVERY_REPORT_CUSTOMER]: {
    rowNumber: CUSTOMER_ORDER_BY
  },
  [ListViews.LOG_CHANGE]: {
    rowNumber: CUSTOMER_ORDER_BY
  }
};

export const APP_ENV = process.env.APP_ENV || "DEV"; // DEV SIT UAT PROD
export const IDLE_TIMEOUT_MINUTE = +process.env.IDLE_TIMEOUT_MINUTE;

export const APP_VERSION = packageInfo.version;

export const DEMO_MODE = process.env.DEMO_MODE === "true";
export const DEMO_EMAIL = process.env.DEMO_EMAIL || "";
export const DEMO_PASSWORD = process.env.DEMO_PASSWORD || "";
export const GTM_ID = process.env.GTM_ID;

export const DEFAULT_NOTIFICATION_SHOW_TIME = 3000;

export const DD_APPLICATION_ID = process.env.DD_APPLICATION_ID;
export const DD_CLIENT_TOKEN = process.env.DD_CLIENT_TOKEN;
export const DD_SITE = process.env.DD_SITE;
export const DD_SESSION_SAMPLE_RATE = +process.env.DD_SESSION_SAMPLE_RATE || 0;
export const DD_SESSION_REPLAY_SAMPLE_RATE =
  +process.env.DD_SESSION_REPLAY_SAMPLE_RATE || 0;

export const CLOUDFLARE_TURNSTILE_SITE_KEY =
  process.env.CLOUDFLARE_TURNSTILE_SITE_KEY;
export const CLOUDFLARE_TURNSTILE_IS_ENABLED = !!CLOUDFLARE_TURNSTILE_SITE_KEY;
export const SHOW_COOKIES_BANNER = process.env.SHOW_COOKIES_BANNER === "true";
export const ANNOUNCE_UNTIL_DATE = process.env.ANNOUNCE_UNTIL_DATE;
export const ANNOUNCE_MESSAGE_DATE = process.env.ANNOUNCE_MESSAGE_DATE;
export const CASHSALE_UNDERPAYMENT_LIMIT = Number(
  process.env.CASHSALE_UNDERPAYMENT_LIMIT ?? -10
);

const REGION = process.env.REGION;
const IDENTITY_POOL_REGION = process.env.IDENTITY_POOL_REGION;
const USER_POOL_ID = process.env.USER_POOL_ID;
const USER_POOL_WEB_CLIENT_ID = process.env.USER_POOL_WEB_CLIENT_ID;
const DOMAIN = process.env.DOMAIN;
const REDIRECT_SIGN_IN = process.env.REDIRECT_SIGN_IN;
const REDIRECT_SIGN_OUT = process.env.REDIRECT_SIGN_OUT;

export const AWS_CONFIG = {
  Auth: {
    // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
    // identityPoolId: 'sa-east-1:eb2df399-09a0-46b4-b37b-d43922e1ef73',
    // REQUIRED - Amazon Cognito Region
    region: REGION,
    // OPTIONAL - Amazon Cognito Federated Identity Pool Region
    // Required only if it's different from Amazon Cognito Region
    identityPoolRegion: IDENTITY_POOL_REGION,
    // OPTIONAL - Amazon Cognito User Pool ID
    userPoolId: USER_POOL_ID,
    // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
    userPoolWebClientId: USER_POOL_WEB_CLIENT_ID
  },
  // OPTIONAL - Hosted UI configuration
  oauth: {
    domain: DOMAIN,
    // scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
    redirectSignIn: REDIRECT_SIGN_IN,
    redirectSignOut: REDIRECT_SIGN_OUT,
    responseType: "code" // or 'token', note that REFRESH token will only be generated when the responseType is code
  }
};

export const STAFF_UPDATE = {
  UPDATE_CART: 1,
  UPDATE_ORDER: 2,
  ADD_ORDER_DOMESTIC: 3
};

export const REGEX_ONLY_NUMBER_CHARACTER = /^[A-Za-z0-9]*$/;
export const REGEX_ONLY_NUMBER = /^\d*$/;
